import type { ComponentPropsWithoutRef, ReactNode } from "react";
import React, { useMemo } from "react";

import type { TypographySizes } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Checkbox,
  RoundedBox,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useUser } from "@js/apps/common/hooks";
import { useGetEmployerPublicProfileQuery } from "@js/apps/employer/api";
import { EmployerLogoNameAndRating } from "@js/apps/employer/components";
import { OfferDetailsUpdateHistoryTable } from "@js/apps/jobs/apps/offers/components/details/update-history-table";
import {
  getLabelForWorkingHourOption,
  showOfferExpiresSection,
} from "@js/apps/jobs/apps/offers/utils";
import { getJobTypeTagMeta } from "@js/apps/jobs/components";
import { DateComponent } from "@js/components/date";
import type { EmployerOffer, FreelancerOffer, Job } from "@js/types/jobs";
import {
  assertUnreachable,
  dateDifferenceFromNow,
  formatCurrency,
  getEnumLabel,
  pluralize,
} from "@js/utils";
import type { IsoDate } from "@js/utils/date/types";
import { DateFormats } from "@js/utils/date/types";

import { JOB_PAYMENT_TYPE_LABELS } from "../../constants";
import { openContractTermsPreviewModal } from "../boilerplate-offer-terms-section";

import { OfferDetailsHeader } from "./header";

import styles from "./style.module.scss";

export type TalentOrEmployerOffer = EmployerOffer | FreelancerOffer;

type OfferDetailsProps = {
  job: Job;
  offer: TalentOrEmployerOffer;
  showUpdatedLabel?: boolean;
};

type DetailMetaContextType = {
  showUpdatedLabel: boolean;
  detailTypographySize?: TypographySizes;
};
const DetailComponentMetaContext = React.createContext<DetailMetaContextType>({
  showUpdatedLabel: false,
  detailTypographySize: "large",
});

const useDetailMetaContext = () => {
  const context = React.useContext(DetailComponentMetaContext);

  if (!context) {
    throw new Error(
      "useDetailMetaContext must be in scope of UpdatedContext provider",
    );
  }

  return context;
};

export const OfferDetails = ({
  offer,
  job,
  showUpdatedLabel = false,
}: OfferDetailsProps) => {
  let component: ReactNode;
  const contextValue = useMemo(
    () => ({ showUpdatedLabel }),
    [showUpdatedLabel],
  );

  switch (job.job_type) {
    case ENUMS.JobType.DIRECT_HIRE:
    case ENUMS.JobType.FREELANCE:
    case ENUMS.JobType.GRANT: {
      component = (
        <RoundedBox sx={{ position: "relative" }}>
          <OfferDetailsHeader offer={offer} />
          <OfferDetailsFacade offer={offer} job={job} />
          <OfferDetailsUpdateHistoryTable offer={offer} />
        </RoundedBox>
      );
      break;
    }
    default: {
      assertUnreachable(job.job_type);
      component = null;
    }
  }

  return (
    <DetailComponentMetaContext.Provider value={contextValue}>
      {component}
    </DetailComponentMetaContext.Provider>
  );
};

type OfferForBYOTContractTermsDetailProps = {
  offer: FreelancerOffer;
  showTermsRequiredError: boolean;
  contractTermsAcceptedAt: null | string;
  handleAcceptClick: (value: null | string) => void;
};

const OfferForBYOTContractTermsDetail = ({
  offer,
  showTermsRequiredError,
  contractTermsAcceptedAt,
  handleAcceptClick,
}: OfferForBYOTContractTermsDetailProps) => {
  const { contract_terms: contractTerms } = offer;
  if (!contractTerms) {
    return <></>;
  }

  const handleChange = (_: unknown, checked: boolean) => {
    handleAcceptClick(!checked ? null : new Date().toISOString());
  };
  return (
    <Box>
      <Typography variant="label" size="small" component="h3" color="grey-1">
        Contract terms{" "}
      </Typography>
      <Stack sx={{ gap: 1 }}>
        <Typography component="p">{contractTerms.description}</Typography>
        <Checkbox
          label=<>
            I Agree to the{" "}
            <Typography
              component="span"
              role="button"
              sx={{ textDecoration: "underline" }}
              onClick={(e) => {
                e.stopPropagation();
                openContractTermsPreviewModal(contractTerms);
              }}
            >
              Contract terms
            </Typography>
          </>
          checked={!!contractTermsAcceptedAt}
          onChange={handleChange}
          error={showTermsRequiredError ? "Must be accepted." : undefined}
          data-testid="contract-terms-checkbox"
        />
      </Stack>
    </Box>
  );
};

export type OfferDetailsForBYOTSignUpProps =
  OfferForBYOTContractTermsDetailProps;

export const OfferDetailsForBYOTSignUp = (
  props: OfferDetailsForBYOTSignUpProps,
) => {
  const { offer } = props;
  const { data: employerData } = useGetEmployerPublicProfileQuery(
    offer.job.employer.id,
  );

  const contextValue: DetailMetaContextType = useMemo(
    () => ({ showUpdatedLabel: false, detailTypographySize: "medium" }),
    [],
  );

  if (!employerData) {
    return null;
  }

  return (
    <DetailComponentMetaContext.Provider value={contextValue}>
      <RoundedBox sx={{ border: "1px solid var(--soft-grey)" }}>
        <OfferDetailsHeader offer={offer} smallerTypography />
        <Box sx={{ my: 3 }}>
          <EmployerLogoNameAndRating
            employer={employerData}
            noUrl
            nameStyle={null}
            logoSize="medium"
            ratingColor="violet"
            ratingIconSize="small"
          />
        </Box>
        <Stack sx={{ gap: 3 }}>
          <PayRateDetail offer={offer} />
          <PlannedDatesDetail offer={offer} />
          <CommitmentDetail offer={offer} />
          <OfferForBYOTContractTermsDetail {...props} />
        </Stack>
      </RoundedBox>
    </DetailComponentMetaContext.Provider>
  );
};

type OfferDetailsFacadeProps = {
  offer: TalentOrEmployerOffer;
  job: Job;
};

const OfferDetailsFacade = ({ offer, job }: OfferDetailsFacadeProps) => {
  const showOfferExpires = showOfferExpiresSection(offer?.status);
  return (
    <DetailsLayoutContainer sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <JobTypeDetal job={job} />
        <PayRateDetail offer={offer} />
        <PlannedDatesDetail offer={offer} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CommitmentDetail offer={offer} />
        {showOfferExpires && <OfferExpiresDetail offer={offer} />}
      </Box>
    </DetailsLayoutContainer>
  );
};

type DetailsLayoutContainerProps = ComponentPropsWithoutRef<typeof Box> & {
  children: ReactNode;
};

const DetailsLayoutContainer = (props: DetailsLayoutContainerProps) => {
  return <Box className={styles.layout} {...props} />;
};

type JobTypeDetalProps = {
  job: Job;
};

const JobTypeDetal = ({ job }: JobTypeDetalProps) => {
  const user = useUser();

  const label = getJobTypeTagMeta(job.job_type, user?.account_type);

  return <Detail label="Job type" value={label} />;
};

type OfferDetailProps = {
  offer: TalentOrEmployerOffer;
};

const PayRateDetail = ({ offer }: OfferDetailProps) => {
  const amount = offer.payment_amount;
  const paymentType = offer.payment_type;

  const isUpdated =
    offer.updated_fields.includes("payment_amount") ||
    offer.updated_fields.includes("payment_type");

  return (
    <Detail
      updated={isUpdated}
      label="Pay rate"
      value={`${formatCurrency(amount)} ${getEnumLabel(
        JOB_PAYMENT_TYPE_LABELS,
        paymentType,
      )}`}
    />
  );
};

const PlannedDatesDetail = ({ offer }: OfferDetailProps) => {
  const startDate = offer.proposed_start_date as IsoDate;
  const endDate = offer.proposed_end_date as IsoDate;
  const content = (
    <>
      {startDate && (
        <>
          <DateComponent date={startDate} format={DateFormats["Jan 1, 1970"]} />
          {" - "}
        </>
      )}
      {endDate && (
        <>
          Ends{" "}
          <DateComponent date={endDate} format={DateFormats["Jan 1, 1970"]} />
        </>
      )}
    </>
  );

  const isUpdated =
    offer.updated_fields.includes("proposed_start_date") ||
    offer.updated_fields.includes("proposed_end_date");

  return <Detail updated={isUpdated} label="Dates" value={content} />;
};

const CommitmentDetail = ({ offer }: OfferDetailProps) => {
  return (
    <Detail
      updated={offer.updated_fields.includes("anticipated_weekly_hours")}
      label="Commitment"
      value={getLabelForWorkingHourOption({
        option: offer.hours_limit_type ?? ENUMS.HoursLimitType.PER_WEEK,
        anticipatedHours: String(offer.anticipated_weekly_hours),
        withHours: true,
      })}
    />
  );
};

const OfferExpiresDetail = ({ offer }: OfferDetailProps) => {
  const date = offer.time_of_expiry as IsoDate;
  const timeOfExpiry = dateDifferenceFromNow(offer.time_of_expiry, "days");
  const expireString = (
    <>
      {timeOfExpiry > 0 && (
        <>
          In {timeOfExpiry} day{pluralize(timeOfExpiry)} |{" "}
        </>
      )}
      <DateComponent date={date} format={DateFormats["Jan 1, 1970"]} />
    </>
  );

  return <Detail label="Offer expires" value={expireString} />;
};

type DetailProps = {
  label: string;
  value: ReactNode;
  updated?: boolean;
};

const Detail = ({ label, value, updated }: DetailProps) => {
  const { showUpdatedLabel, detailTypographySize } = useDetailMetaContext();

  const _label =
    showUpdatedLabel && updated ? (
      <>
        {label} - <span className="positive-2">Updated</span>
      </>
    ) : (
      label
    );

  return (
    <Box>
      <Typography variant="label" size="small" component="h3" color="grey-1">
        {_label}
      </Typography>
      <Typography size={detailTypographySize || "large"} component="p">
        {value}
      </Typography>
    </Box>
  );
};
